import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, AlertCircle, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { useSettings } from '../contexts/SettingsContext';
import api from '../services/api';
import { metricsTemplatesService } from '../services';
import ReportPreviewContent from '../components/ReportPreviewContent';
import { getApiError } from '@/lib/utils';

const SummaryReportViewPage = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [report, setReport] = useState(null);
  const [capturedMetrics, setCapturedMetrics] = useState([]);
  const [metricsTemplate, setMetricsTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const resp = await api.get(`/summary-reports/${reportId}`);
        setReport(resp.data);
        
        // Use captured metrics from main response (included by backend)
        const metricsFromResponse = resp.data.captured_metrics || [];
        if (metricsFromResponse.length > 0) {
          setCapturedMetrics(metricsFromResponse);
          setMetricsTemplate({ title: resp.data.metrics_template_title || metricsFromResponse[0].template_title_snapshot });
        } else {
          // Fallback: try separate endpoint
          try {
            const metrics = await metricsTemplatesService.getCapturedMetrics(reportId);
            if (metrics && metrics.length > 0) {
              setCapturedMetrics(metrics);
              setMetricsTemplate({ title: metrics[0].template_title_snapshot });
            } else {
              // No captured metrics — check if client qualifies for a template
              // If so, show empty template with nil values
              const siteId = resp.data.site_id;
              if (siteId) {
                try {
                  const resolved = await metricsTemplatesService.resolve(siteId);
                  if (resolved && resolved.lines && resolved.lines.length > 0) {
                    setMetricsTemplate({ title: resolved.title });
                    setCapturedMetrics(resolved.lines.filter(l => l.is_active !== false).map(l => ({
                      metric_label_snapshot: l.metric_label,
                      metric_type_snapshot: l.metric_type || 'PERCENTAGE',
                      display_order_snapshot: l.display_order || 0,
                      note_enabled_snapshot: l.note_enabled || false,
                      this_period_value: null,
                      last_period_value: null,
                      note: null,
                    })));
                  }
                } catch { /* No template for this client */ }
              }
            }
          } catch { /* No captured metrics */ }
        }
      } catch (e) {
        setError(getApiError(e, 'Failed to load report'));
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, [reportId]);

  const handleExportPdf = async () => {
    setIsExporting(true);
    toast.info('Generating PDF...', { id: 'pdf-export', duration: 30000 });
    try {
      const resp = await api.get(`/summary-reports/${reportId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Summary_Report_${reportId}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded', { id: 'pdf-export' });
    } catch (e) {
      console.error('PDF download failed:', e);
      toast.error('PDF download failed', { id: 'pdf-export' });
    } finally {
      setIsExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-8 w-8 mx-auto mb-3 text-red-500" />
        <p className="text-sm text-muted-foreground">{error}</p>
        <Button variant="outline" size="sm" className="mt-3" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
      </div>
    );
  }

  if (!report) return null;

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="summary-report-view">
      {/* Navigation header */}
      <div className="flex items-center justify-between mb-4 print:hidden">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <div className="flex items-center gap-2">
          {/* Only surface the AP route when the client/group actually has AP
              enabled (ap_eligible=true). Hiding it when AP is OFF prevents
              the false impression that an AP can be created or exists. Read-
              only access to genuine existing plans is preserved via the
              /action-plans/:planId direct route when relevant. */}
          {report?.ap_eligible && (
            <Button variant="outline" size="sm" onClick={() => navigate(`/summary-reports/${reportId}/action-plan`)} data-testid="view-action-plan-btn">
              Action Plan
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => navigate(`/summary-reports/${reportId}`)} data-testid="edit-report-btn">
            <Eye className="h-4 w-4 mr-1" /> Open in Wizard
          </Button>
        </div>
      </div>

      {/* Shared Report Preview Content - Single Source of Truth */}
      <ReportPreviewContent
        report={report}
        settings={settings}
        showExportPdf={true}
        onExportPdf={handleExportPdf}
        isExporting={isExporting}
        capturedMetrics={capturedMetrics}
        metricsTemplate={metricsTemplate}
      />
    </div>
  );
};

export default SummaryReportViewPage;
